import { useEffect, useMemo } from 'react';
import { Check, X } from 'lucide-react';
import { useThemeStore } from '../../stores/theme.store';

const COMMON_PASSWORDS = new Set([
  '123456',
  'senha',
  'password',
  '123456789',
  'admin',
  '12345678',
  'abc123',
  'qwerty',
  '1234567',
  '12345',
  '111111',
  'senha123',
]);

export interface PasswordRuleCheck {
  /** Technical key of the rule (stable across locales). */
  id: string;
  /** Human-readable label shown in the UI. */
  label: string;
  /** Whether the rule is satisfied by the current password. */
  passed: boolean;
  /**
   * If true, failing this rule does NOT block the user.
   * Kept visible so the user knows this rule exists.
   */
  optional?: boolean;
}

export interface PasswordStrengthIndicatorProps {
  password: string;
  onValidChange?: (valid: boolean) => void;
  /**
   * When true, the component shows a warning when the password is in the
   * common passwords blacklist. Defaults to true.
   */
  checkCommonPasswords?: boolean;
  className?: string;
}

type Strength = 'empty' | 'weak' | 'medium' | 'strong';

function evaluatePassword(password: string): {
  rules: PasswordRuleCheck[];
  requiredPassed: number;
  requiredTotal: number;
  isCommon: boolean;
} {
  const trimmed = password ?? '';
  const lower = trimmed.toLowerCase();

  const rules: PasswordRuleCheck[] = [
    {
      id: 'min-length',
      label: 'Mínimo 8 caracteres',
      passed: trimmed.length >= 8,
    },
    {
      id: 'uppercase',
      label: 'Uma letra maiúscula',
      passed: /[A-Z]/.test(trimmed),
    },
    {
      id: 'lowercase',
      label: 'Uma letra minúscula',
      passed: /[a-z]/.test(trimmed),
    },
    {
      id: 'number',
      label: 'Um número',
      passed: /\d/.test(trimmed),
    },
    {
      id: 'special',
      label: 'Um caractere especial (opcional)',
      passed: /[^A-Za-z0-9]/.test(trimmed),
      optional: true,
    },
  ];

  const requiredRules = rules.filter((r) => !r.optional);
  const requiredPassed = requiredRules.filter((r) => r.passed).length;

  return {
    rules,
    requiredPassed,
    requiredTotal: requiredRules.length,
    isCommon: COMMON_PASSWORDS.has(lower),
  };
}

function computeStrength(
  password: string,
  requiredPassed: number,
  isCommon: boolean,
): Strength {
  if (!password) return 'empty';
  if (isCommon) return 'weak';
  if (requiredPassed <= 2) return 'weak';
  if (requiredPassed === 3) return 'medium';
  return 'strong';
}

export function PasswordStrengthIndicator({
  password,
  onValidChange,
  checkCommonPasswords = true,
  className,
}: PasswordStrengthIndicatorProps) {
  const { isDarkMode } = useThemeStore();

  const evaluation = useMemo(() => evaluatePassword(password), [password]);
  const { rules, requiredPassed, requiredTotal, isCommon } = evaluation;
  const strength = computeStrength(password, requiredPassed, isCommon);

  const isValid =
    requiredPassed === requiredTotal &&
    (!checkCommonPasswords || !isCommon);

  useEffect(() => {
    onValidChange?.(isValid);
  }, [isValid, onValidChange]);

  const barColor =
    strength === 'strong'
      ? 'bg-green-500'
      : strength === 'medium'
        ? 'bg-yellow-500'
        : strength === 'weak'
          ? 'bg-red-500'
          : 'bg-surface-700';

  const barWidth =
    strength === 'strong'
      ? 'w-full'
      : strength === 'medium'
        ? 'w-2/3'
        : strength === 'weak'
          ? 'w-1/3'
          : 'w-0';

  const strengthLabel =
    strength === 'strong'
      ? 'Forte'
      : strength === 'medium'
        ? 'Média'
        : strength === 'weak'
          ? 'Fraca'
          : '';

  const strengthLabelClass =
    strength === 'strong'
      ? 'text-green-500'
      : strength === 'medium'
        ? 'text-yellow-500'
        : strength === 'weak'
          ? 'text-red-500'
          : 'text-surface-500';

  const mutedClass = isDarkMode ? 'text-surface-400' : 'text-gray-500';
  const labelClass = isDarkMode ? 'text-surface-200' : 'text-gray-800';
  const trackClass = isDarkMode ? 'bg-surface-800' : 'bg-gray-200';

  return (
    <div className={className ?? 'space-y-2'}>
      {/* Strength bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className={`text-xs ${mutedClass}`}>Força da senha</span>
          <span className={`text-xs font-medium ${strengthLabelClass}`}>
            {strengthLabel}
          </span>
        </div>
        <div className={`h-1.5 rounded-full overflow-hidden ${trackClass}`}>
          <div
            className={`h-full transition-all duration-300 ease-out ${barColor} ${barWidth}`}
          />
        </div>
      </div>

      {/* Common password warning */}
      {checkCommonPasswords && isCommon && (
        <div className="flex items-center gap-2 text-xs text-red-500">
          <X className="w-3.5 h-3.5" />
          <span>Essa senha é muito comum. Escolha outra.</span>
        </div>
      )}

      {/* Rules list */}
      <ul className="space-y-1">
        {rules.map((rule) => (
          <li
            key={rule.id}
            className={`flex items-center gap-2 text-xs ${
              rule.passed ? 'text-green-500' : mutedClass
            }`}
          >
            {rule.passed ? (
              <Check className="w-3.5 h-3.5 flex-shrink-0" />
            ) : (
              <X
                className={`w-3.5 h-3.5 flex-shrink-0 ${
                  rule.optional ? 'text-surface-500' : 'text-red-400'
                }`}
              />
            )}
            <span className={rule.passed ? labelClass : ''}>{rule.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default PasswordStrengthIndicator;
